import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { apiRequest } from '../../auth/msalConfig';
import Layout from '../../components/Layout/Layout';
import {
  MdUploadFile,
  MdInsertDriveFile,
  MdClose,
  MdFileDownload,
  MdContentCopy,
  MdCheckCircle,
  MdSend,
  MdStop,            // [P0] Stop generating
  MdAdd,
  MdCheck,           // [P0] copy-per-message "Copied" state
  MdRefresh,         // [P0] Retry on error bubbles
  MdArrowDownward,   // [P0] scroll-to-bottom pill
  MdDescription,
  MdSettings,
  MdArticle,
  MdInventory2,
  MdCloudUpload,
  MdErrorOutline,
  MdKeyboardArrowRight,   // collapse/expand caret for the STEP JSON block
} from '../../utils/icons';
import AttributeConfig from './AttributeConfig';
import PromptConfig from './PromptConfig';
import Tier2Config from './Tier2Config';
import { isStreamingResponse, readN8nStream, StreamEvent } from './n8nStream';
import { unwrapEnvelope, StepApiError, fetchWithRetry, TRANSIENT_STATUS, TRANSIENT_MESSAGE } from './stepApi';
import { Can } from '../../components/PermissionGuard';
import { useAuthorization } from '../../auth/useAuthorization';
import { Roles } from '../../auth/roles';
import { allowedOpcos } from '../../auth/opcos';
import { describeStepError } from './stepErrors';
import './StepProductLinq.css';

// ── Types ────────────────────────────────────────────────────────────────────

interface ChatMsg {
  id: string;
  role: 'user' | 'agent';
  text: string;            // user text, or agent markdown (output)
  timestamp: string;
  fileName?: string;       // attached file name (extraction user-turn)
  opcoUsed?: string;       // OpCo selected (extraction user-turn)
  payload?: unknown;       // response `json` field — Copy / Download / tree
  hints?: string[];        // agent-suggested follow-ups (response `hints`)
  isError?: boolean;       // [P0] render as an error bubble + Retry
}

interface ParsedResult {
  text: string;            // `output` markdown
  json?: unknown;          // `json` field if the agent returned full result
  hints?: string[];        // `hints` array if the agent returned suggestions
}

// `data` of a successful POST /webhook/step-product-upload (contract 2026-08-03).
// `dropped_containers` was removed in that release — it had been hard-coded to 0.
interface UploadResult {
  message?: string;
  product_count?: number;
  background_process_id?: string;
  packaging_tier_count?: number;
}

// Per-result state of the "Post to STEP" upload (keyed by message id).
interface PostState {
  status: 'posting' | 'done' | 'error';
  message?: string;
}

// Live progress step streamed by the workflow while a turn is running.
interface LiveStep {
  stage: string;
  label: string;
  detail?: string;
  done: boolean;
}

// [P0] What a turn needs to be re-run by Retry.
interface LastRequest {
  text: string;
  file: File | null;
  opco: string;
}

// ── Config ───────────────────────────────────────────────────────────────────

const ACCEPT = '.xlsx,.xls,.csv,.pdf';
const ACCEPT_EXT = ['.xlsx', '.xls', '.csv', '.pdf']; // [P0] enforced for drag/paste
const MAX_BYTES = 25 * 1024 * 1024;
const EXTRACT_PROMPT = 'Please extract this supplier document.';
const NEAR_BOTTOM_PX = 120; // [P0] auto-scroll threshold

// Once a STEP payload is generated, edits to it via chat are not allowed — the
// values are locked to stay in sync with the extraction. Text turns matching
// this intent are answered with a canned explanation instead of hitting n8n.
const CHANGE_INTENT_RE =
  /\b(change|changes|changing|changed|updat(?:e|es|ing|ed)|modif(?:y|ies|ying|ied)|edit(?:s|ing|ed)?|replac(?:e|es|ing|ed)|adjust(?:s|ing|ed)?|alter(?:s|ing|ed)?|revis(?:e|es|ing|ed)|amend(?:s|ing|ed)?|correct(?:s|ing|ed)?|overwrit(?:e|es|ing|ten)|rewrit(?:e|es|ing|ten)|set\s+the|make\s+it|fix\s+the)\b/i;

const CHANGE_BLOCKED_MSG =
  "⚠️ **Editing the STEP payload here isn't supported.**\n\n" +
  'Once the **Step Product Details** are generated, the values are locked so they stay in sync with the extraction. ' +
  'You can still ask questions about the result.\n\n' +
  'To produce different values, start a **New Chat** and re-extract the document, or adjust the data in STEP after posting.';

// ── Generic helpers ──────────────────────────────────────────────────────────

const formatTime = (d: Date) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const newUUID = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const fileSlug = (s: string): string =>
  s.trim().replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9._-]+/g, '_').replace(/^_+|_+$/g, '') || 'na';

const fileStamp = (d: Date): string => {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
};

const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const pick = (obj: Record<string, unknown> | undefined, keys: string[]): unknown => {
  if (!obj) return undefined;
  const lower = new Map(Object.keys(obj).map((k) => [k.toLowerCase(), k]));
  for (const k of keys) {
    const real = lower.get(k.toLowerCase());
    if (real !== undefined && obj[real] !== undefined && obj[real] !== null) return obj[real];
  }
  return undefined;
};

/**
 * Removes the attributes detail list from the text output, preserving
 * statistic summary headers and warning messages.
 */
const cleanExtractionText = (text: string): string => {
  if (!text) return text;

  // 1. If the block is complete: remove it and keep any text after it.
  const completeRegex = /Key\s*(?:mapped\s*)?STEP attributes captured:\s*(?:\n\s*[*_]*att_[a-zA-Z0-9_]+[*_]*:[^\n]*)+(?:\n\s*Packaging tiers \/ data containers:\s*none[^\n]*)?/gi;
  if (completeRegex.test(text)) {
    let cleaned = text.replace(completeRegex, '');
    cleaned = cleaned.replace(/\n{3,}/g, '\n\n').trim();
    return cleaned;
  }

  // 2. If the block has started but is not complete yet (streaming): remove it and everything after it.
  const partialRegex = /Key\s*(?:mapped\s*)?STEP attributes captured:\s*(?:\n\s*[*_]*att_[a-zA-Z0-9_]+[*_]*:[^\n]*)*/gi;
  if (partialRegex.test(text)) {
    let cleaned = text.replace(partialRegex, '');
    cleaned = cleaned.replace(/\n{3,}/g, '\n\n').trim();
    return cleaned;
  }

  return text;
};



// ── STEP payload helpers (product id capture) ─────────────────────────────────
// `values[].value` packs attributes as "att_Key||val;att_Key||val" — unpack to a map.

const parseMultivalue = (v: string): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const pair of v.split(';')) {
    const i = pair.indexOf('||');
    if (i > -1) out[pair.slice(0, i).trim()] = pair.slice(i + 2).trim();
  }
  return out;
};

// First att_Name found across a product's value rows — the human label for the row.
const productName = (p: Record<string, unknown>): string => {
  const values = Array.isArray(p.values) ? p.values : [];
  for (const v of values) {
    if (isObj(v) && typeof v.value === 'string') {
      const m = parseMultivalue(v.value);
      if (m['att_Name']) return m['att_Name'];
    }
  }
  return '';
};

// The agent invents a placeholder product id (e.g. "opcoItem-<product name>"), but the
// real item id only exists in the external STEP system. Blank it on ingest so the capture
// panel starts empty and "Post to STEP" stays disabled until the user applies real ids.
const stripProductIds = (payload: unknown): unknown => {
  if (!isObj(payload) || !Array.isArray(payload['products'])) return payload;
  const products = (payload['products'] as unknown[]).map((p) => (isObj(p) && 'id' in p ? { ...p, id: '' } : p));
  return { ...payload, products };
};

// Products array off the STEP payload (null when the payload isn't an extraction result).
const getProducts = (payload: unknown): Record<string, unknown>[] | null => {
  if (!isObj(payload)) return null;
  const ps = payload['products'];
  if (!Array.isArray(ps) || ps.length === 0) return null;
  const objs = ps.filter(isObj) as Record<string, unknown>[];
  return objs.length > 0 ? objs : null;
};

// ── Response parser ──────────────────────────────────────────────────────────
// Contract: { "output": "markdown" } and optionally { "json": { ... } }.
// n8n may wrap the body in an array — unwrap the first element.

const parseResult = async (res: Response): Promise<ParsedResult> => {
  // Read the body once as text, then try to parse. Calling res.json() and then
  // res.text() on the same Response throws "body stream already read".
  const raw = await res.text();
  let body: unknown;
  try { body = JSON.parse(raw); } catch { return { text: raw }; }

  const root: unknown = Array.isArray(body) && body.length > 0 ? body[0] : body;
  const obj = isObj(root) ? root : undefined;

  let text = '';
  const outV = pick(obj, ['output', 'text', 'message', 'response', 'result']);
  if (typeof outV === 'string') text = outV;
  else if (typeof root === 'string') text = root;

  const jsonV = pick(obj, ['json']);
  const hintsV = pick(obj, ['hints', 'suggestions', 'followups', 'follow_ups']);
  const hints = Array.isArray(hintsV) ? hintsV.filter((h): h is string => typeof h === 'string').slice(0, 4) : undefined;
  return { text, ...(jsonV !== undefined && { json: jsonV }), ...(hints && hints.length > 0 && { hints }) };
};

// ── Markdown renderer (agent output) ──────────────────────────────────────────

const EMOJI_MAP: Record<string, string> = {
  ':white_check_mark:': '✅', ':x:': '❌', ':warning:': '⚠️', ':arrows_counterclockwise:': '🔄',
  ':wave:': '👋', ':robot_face:': '🤖', ':mag:': '🔍', ':clipboard:': '📋',
  ':information_source:': 'ℹ️', ':check:': '✔️', ':tada:': '🎉', ':hourglass:': '⏳',
};
const resolveEmojis = (text: string): string =>
  Object.entries(EMOJI_MAP).reduce((s, [code, emoji]) => s.replaceAll(code, emoji), text);

const renderInline = (text: string): React.ReactNode[] => {
  const segments = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g);
  return segments.map((seg, i) => {
    if (seg.startsWith('**') && seg.endsWith('**')) return <strong key={i}>{seg.slice(2, -2)}</strong>;
    if (seg.startsWith('*') && seg.endsWith('*')) return <em key={i}>{seg.slice(1, -1)}</em>;
    if (seg.startsWith('`') && seg.endsWith('`')) return <code key={i} className="spl-md-code">{seg.slice(1, -1)}</code>;
    return <React.Fragment key={i}>{seg}</React.Fragment>;
  });
};

// Render one markdown line to a node (shared by top level and collapsible bodies).
const renderMdLine = (raw: string, i: number): React.ReactNode => {
  const line = raw.trimEnd();
  const t = line.trim();
  if (t === '') return <div key={i} className="spl-md-spacer" />;
  if (t === '---' || t === '***') return <hr key={i} className="spl-md-hr" />;
  const h = /^(#{1,4})\s+(.*)$/.exec(t);
  if (h) return <div key={i} className={`spl-md-h spl-md-h${h[1].length}`}>{renderInline(h[2])}</div>;
  const b = /^[-*]\s+(.*)$/.exec(t);
  if (b) return <div key={i} className="spl-md-li">{renderInline(b[1])}</div>;
  const n = /^(\d+)\.\s+(.*)$/.exec(t);
  if (n) return <div key={i} className="spl-md-li spl-md-li--num"><span className="spl-md-num">{n[1]}.</span>{renderInline(n[2])}</div>;
  return <div key={i} className="spl-md-p">{renderInline(line)}</div>;
};

// Section headers the user can collapse to save chat space. Leading emoji /
// markdown decoration (#, **) is tolerated before the title word.
const COLLAPSIBLE_RE = /^[^A-Za-z]*\*{0,2}\s*(validation warnings?|statistics)\b/i;

// Stat-summary fields that belong under Statistics, not Validation warnings.
// Tolerates leading bullet / markdown decoration before the key.
const STAT_FIELD_RE = /^[^A-Za-z]*[-*]?\s*[*_]*\s*(overall_confidence|review_required)\b/i;

// A line that opens a new block — ends the body of an open collapsible section.
const isHeadingLike = (t: string): boolean =>
  t === '---' || t === '***' ||
  /^#{1,4}\s+/.test(t) ||
  /^\*\*[^*]+\*\*:?\s*$/.test(t) ||
  COLLAPSIBLE_RE.test(t);

const sectionLabel = (t: string): string => {
  const m = COLLAPSIBLE_RE.exec(t);
  const raw = (m?.[1] ?? '').toLowerCase();
  return raw.startsWith('validation') ? 'Validation warnings' : 'Statistics';
};

type MdBlock =
  | { kind: 'lines'; lines: string[]; start: number }
  | { kind: 'section'; label: string; body: string[]; start: number };

// Number the content lines of a section body; blanks/rules pass through untouched.
// Strips any pre-existing bullet or number so numbering is sequential and clean.
const numberBody = (body: string[]): string[] => {
  let n = 0;
  return body.map((raw) => {
    const t = raw.trim();
    if (t === '' || t === '---' || t === '***') return raw;
    n += 1;
    return `${n}. ${t.replace(/^(\d+\.\s+|[-*]\s+)/, '')}`;
  });
};

// Group lines into plain runs and collapsible sections.
const groupMdBlocks = (lines: string[]): MdBlock[] => {
  const blocks: MdBlock[] = [];
  const seenLabels = new Set<string>();
  const relocated: string[] = []; // stat fields lifted out of Validation warnings
  let i = 0;
  while (i < lines.length) {
    const t = lines[i].trim();
    if (COLLAPSIBLE_RE.test(t)) {
      const label = sectionLabel(t);
      const body: string[] = [];
      let j = i + 1;
      while (j < lines.length && !isHeadingLike(lines[j].trim())) {
        // Lift stat fields out of Validation warnings so they land under Statistics.
        if (label === 'Validation warnings' && STAT_FIELD_RE.test(lines[j].trim())) relocated.push(lines[j]);
        else body.push(lines[j]);
        j++;
      }
      // Keep only the first section per label; drop duplicate blocks (header + body).
      if (!seenLabels.has(label)) {
        seenLabels.add(label);
        blocks.push({ kind: 'section', label, body, start: i });
      }
      i = j;
    } else {
      const run: string[] = [];
      const start = i;
      while (i < lines.length && !COLLAPSIBLE_RE.test(lines[i].trim())) { run.push(lines[i]); i++; }
      blocks.push({ kind: 'lines', lines: run, start });
    }
  }
  // Append lifted stat fields to the Statistics section (create it if absent so they aren't lost).
  if (relocated.length) {
    const stat = blocks.find((b): b is Extract<MdBlock, { kind: 'section' }> => b.kind === 'section' && b.label === 'Statistics');
    if (stat) stat.body.push(...relocated);
    else blocks.push({ kind: 'section', label: 'Statistics', body: relocated, start: lines.length });
  }
  // Render both collapsible section bodies as numbered lists.
  for (const b of blocks) {
    if (b.kind === 'section') b.body = numberBody(b.body);
  }
  return blocks;
};

const CollapsibleSection: React.FC<{ label: string; body: string[]; startKey: number }> = ({ label, body, startKey }) => {
  const [open, setOpen] = useState(false);
  const bodyId = `spl-md-sect-${startKey}`;
  return (
    <div className="spl-md-section">
      <button
        type="button"
        className={`spl-md-section-toggle${open ? ' is-open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={bodyId}
      >
        <MdKeyboardArrowRight className="spl-md-section-caret" aria-hidden="true" />
        <span>{label}</span>
      </button>
      {open && (
        <div id={bodyId} className="spl-md-section-body">
          {body.map((raw, k) => renderMdLine(raw, k))}
        </div>
      )}
    </div>
  );
};

const MarkdownText: React.FC<{ text: string; collapsible?: boolean }> = ({ text, collapsible = false }) => {
  const lines = resolveEmojis(text).split('\n');
  if (!collapsible) {
    return <div className="spl-md">{lines.map((raw, i) => renderMdLine(raw, i))}</div>;
  }
  const blocks = groupMdBlocks(lines);
  return (
    <div className="spl-md">
      {blocks.map((blk) =>
        blk.kind === 'section' ? (
          <CollapsibleSection key={blk.start} label={blk.label} body={blk.body} startKey={blk.start} />
        ) : (
          blk.lines.map((raw, k) => renderMdLine(raw, blk.start + k))
        ),
      )}
    </div>
  );
};

// ── Collapsible JSON tree viewer ─────────────────────────────────────────────

const primClass = (v: unknown): string =>
  typeof v === 'string' ? 'spl-json-string'
    : typeof v === 'number' ? 'spl-json-number'
      : typeof v === 'boolean' ? 'spl-json-boolean'
        : v === null ? 'spl-json-null' : 'spl-json-punctuation';

const primText = (v: unknown): string =>
  typeof v === 'string' ? `"${v}"` : v === null ? 'null' : String(v);

const JsonNode: React.FC<{ name?: string; nameIsIndex?: boolean; value: unknown; depth: number }> = ({ name, nameIsIndex, value, depth }) => {
  const isArr = Array.isArray(value);
  const isObjVal = value !== null && typeof value === 'object' && !isArr;
  const [open, setOpen] = useState(true);
  const pad = { paddingLeft: `${depth * 16 + 8}px` };

  const keyLabel = name !== undefined && (
    <>
      <span className={nameIsIndex ? 'spl-tree-index' : 'spl-json-key'}>{name}</span>
      <span className="spl-json-punctuation">: </span>
    </>
  );

  if (!isArr && !isObjVal) {
    return (
      <div className="spl-tree-row" style={pad}>
        {keyLabel}<span className={primClass(value)}>{primText(value)}</span>
      </div>
    );
  }

  const entries: [string, unknown][] = isArr
    ? (value as unknown[]).map((v, i) => [String(i), v])
    : Object.entries(value as Record<string, unknown>);
  const openBr = isArr ? '[' : '{';
  const closeBr = isArr ? ']' : '}';
  const count = entries.length;
  const summary = `${count} ${isArr ? (count === 1 ? 'item' : 'items') : (count === 1 ? 'key' : 'keys')}`;

  return (
    <div className="spl-tree-node">
      {/* [A11Y] toggle is a real <button> with aria-expanded */}
      <button type="button" className="spl-tree-row spl-tree-row--toggle" style={pad} onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        {/* larger caret for standard collapse/expand affordance */}
        <span className="spl-tree-caret" aria-hidden="true">{open ? '▾' : '▸'}</span>
        {keyLabel}
        <span className="spl-json-punctuation">{openBr}</span>
        {!open && (
          <span className="spl-tree-collapsed">
            <span className="spl-tree-summary">{summary}</span>
            <span className="spl-json-punctuation">{closeBr}</span>
          </span>
        )}
      </button>
      {open && (
        <>
          {entries.map(([k, v]) => (
            <JsonNode key={k} name={k} nameIsIndex={isArr} value={v} depth={depth + 1} />
          ))}
          <div className="spl-tree-row" style={pad}><span className="spl-json-punctuation">{closeBr}</span></div>
        </>
      )}
    </div>
  );
};

const JsonTree: React.FC<{ data: unknown }> = ({ data }) => (
  <div className="spl-tree"><JsonNode value={data} depth={0} /></div>
);

// ── Product item ID capture panel ────────────────────────────────────────────

// Joins the current ids into one memo key; a control char keeps it unambiguous
// for ids that contain spaces or punctuation.
const ID_SEP = '\u0000';

const ProductIdPanel: React.FC<{
  products: Record<string, unknown>[];
  onApply: (ids: string[]) => void;
}> = ({ products, onApply }) => {
  // `products` is a fresh array on every parent render, so memoise on the id values
  // themselves — otherwise the sync effect below would wipe ids the user is typing.
  const initialKey = products.map((p) => (typeof p['id'] === 'string' ? (p['id'] as string) : '')).join(ID_SEP);
  const initial = useMemo(() => initialKey.split(ID_SEP), [initialKey]);
  const [ids, setIds] = useState<string[]>(initial);
  useEffect(() => { setIds(initial); }, [initial]);

  const dirty = ids.some((v, i) => v !== initial[i]);
  const allFilled = ids.every((v) => v.trim() !== '');
  const setOne = (i: number, val: string) => setIds((prev) => prev.map((v, idx) => (idx === i ? val : v)));

  return (
    <div className="spl-pidpanel">
      <div className="spl-pidpanel-head">
        <span className="spl-pidpanel-sub">
          {products.length} product{products.length === 1 ? '' : 's'} · paste the item id from STEP for each
        </span>
      </div>

      <div className="spl-pidlist">
        {products.map((p, i) => {
          const name = productName(p);
          const company = typeof p['company'] === 'string' ? (p['company'] as string) : '';
          return (
            <div className="spl-pidrow" key={i}>
              <div className="spl-pidinfo">
                <span className="spl-pidnum">#{i + 1}</span>
                <div className="spl-pidmeta">
                  <span className="spl-pidname" title={name}>{name || 'Unnamed product'}</span>
                  {company && <span className="spl-pidcompany">{company}</span>}
                </div>
              </div>
              <label className="spl-pidfield">
                <span className="spl-pidlabel">
                  Product item ID<span className="spl-pidreq" aria-hidden="true">*</span>
                  <span className="spl-sr-only"> (required)</span>
                </span>
                <input
                  className="spl-pidinput"
                  type="text"
                  value={ids[i] ?? ''}
                  onChange={(e) => setOne(i, e.target.value)}
                  placeholder="e.g opcoItem-XXXXXXX"
                  spellCheck={false}
                  autoComplete="off"
                  required
                  aria-required="true"
                  aria-invalid={(ids[i] ?? '').trim() === ''}
                  data-empty={(ids[i] ?? '').trim() === '' ? 'true' : 'false'}
                />
              </label>
            </div>
          );
        })}
      </div>

      <div className="spl-pidactions">
        {!dirty && <span className="spl-pidsaved"><MdCheckCircle aria-hidden="true" />IDs saved to payload</span>}
        <button
          className="spl-action-btn spl-pidapply"
          onClick={() => onApply(ids.map((s) => s.trim()))}
          disabled={!dirty || !allFilled}
          title={!allFilled ? 'Enter an id for every product' : 'Write ids into the STEP JSON'}
        >
          <MdCheckCircle aria-hidden="true" />Apply IDs
        </button>
      </div>
    </div>
  );
};

// ── Nested collapsible sub-panel (title as child) ────────────────────────────

const SubPanel: React.FC<{ title: string; defaultOpen?: boolean; children: React.ReactNode }> = ({ title, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="spl-subpanel">
      <button type="button" className="spl-subpanel-toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <MdKeyboardArrowRight className="spl-subpanel-caret" data-open={open ? 'true' : 'false'} aria-hidden="true" />
        <span>{title}</span>
      </button>
      {open && <div className="spl-subpanel-body">{children}</div>}
    </div>
  );
};

// ── Per-turn result block — shown when the agent returns `json` ───────────────

const ResultBlock: React.FC<{
  payload: unknown;
  copied: boolean;
  postState?: PostState;
  canPost: boolean;
  onCopy: () => void;
  onDownload: () => void;
  onApplyIds: (ids: string[]) => void;
  onPost: () => void;
}> = ({ payload, copied, postState, canPost, onCopy, onDownload, onApplyIds, onPost }) => {
  const products = getProducts(payload);
  const idsMissing = !!products && products.some((p) => typeof p['id'] !== 'string' || !(p['id'] as string).trim());
  const posting = postState?.status === 'posting';
  const posted = postState?.status === 'done';

  return (
    <div className="spl-resblock">
      <div className="spl-resblock-head">
        <span className="spl-resblock-title">Step Product Details</span>
        <span className="spl-status-chip spl-status-chip--ok">
          <MdCheckCircle aria-hidden="true" />Result
        </span>
        <div className="spl-resblock-actions">
          <button type="button" className="spl-action-btn" onClick={onCopy} title="Copy STEP JSON">
            <MdContentCopy aria-hidden="true" />{copied ? 'Copied' : 'Copy'}
          </button>
          <button type="button" className="spl-action-btn" onClick={onDownload}><MdFileDownload aria-hidden="true" />Download</button>
          {products && canPost && (
            <button
              type="button"
              className="spl-action-btn spl-post-btn"
              onClick={onPost}
              disabled={posting || posted || idsMissing}
              title={idsMissing
                ? 'Assign and apply a product item ID for every product first'
                : posted ? 'This payload has already been posted' : 'Post this payload to STEP'}
            >
              {posting
                ? <span className="spl-step-spinner" aria-hidden="true" />
                : posted ? <MdCheckCircle aria-hidden="true" /> : <MdCloudUpload aria-hidden="true" />}
              {posting ? 'Posting…' : posted ? 'Posted' : postState?.status === 'error' ? 'Retry post' : 'Post to STEP'}
            </button>
          )}
        </div>
      </div>
      {postState && postState.status !== 'posting' && (
        <div className={`spl-postnote spl-postnote--${postState.status === 'done' ? 'ok' : 'err'}`} role={postState.status === 'error' ? 'alert' : undefined}>
          {postState.status === 'done'
            ? <MdCheckCircle aria-hidden="true" />
            : <MdErrorOutline aria-hidden="true" />}
          <span>{postState.message || (postState.status === 'done' ? 'Payload posted to STEP.' : 'STEP upload failed.')}</span>
        </div>
      )}
      <div className="spl-resblock-body">
        {products && (
          <SubPanel title="Product Item Ids" defaultOpen>
            <ProductIdPanel products={products} onApply={onApplyIds} />
          </SubPanel>
        )}
        <SubPanel title="Payload Json">
          <JsonTree data={payload} />
        </SubPanel>
      </div>
    </div>
  );
};

// ── Simulated extraction loader ───────────────────────────────────────────────

interface SimStage {
  at: number;
  label: string;
  details: string[];
}

const simStagesFor = (fileName: string, opco: string): SimStage[] => {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  const kind = ext === 'pdf' ? 'PDF' : ext === 'csv' ? 'CSV file' : 'spreadsheet';
  return [
    { at: 0, label: `Uploading ${fileName}`, details: ['Sending the document to the extraction workflow…'] },
    { at: 3, label: `Reading the ${kind}`, details: ['Detecting sheets, headers and layout…', 'Cleaning up empty rows and merged cells…'] },
    { at: 9, label: 'Identifying products', details: ['Looking for product rows and item codes…', 'Grouping variants and pack sizes…'] },
    { at: 17, label: 'Extracting attributes', details: ['Pulling names, descriptions, dimensions and units…', 'Normalising units of measure…', 'Reading pricing and barcode fields…'] },
    { at: 30, label: `Mapping to STEP for ${opco}`, details: ['Matching supplier fields to STEP attributes…', `Applying the ${opco} attribute rules…`] },
    { at: 44, label: 'Validating extracted data', details: ['Checking mandatory attributes…', 'Flagging missing or ambiguous values…'] },
    { at: 58, label: 'Assembling the STEP JSON', details: ['Building the final payload…', 'Almost there — large documents can take a minute or two…'] },
  ];
};

const UnifiedLoader: React.FC<{
  fileName: string;
  opco: string;
  liveSteps: LiveStep[];
  liveText: string;
}> = ({ fileName, opco, liveSteps, liveText }) => {
  const stages = useMemo(() => simStagesFor(fileName, opco), [fileName, opco]);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const isLive = liveSteps.length > 0 || !!liveText;

  // Determine active simulated stage and progress percentage
  let activeIdx = 0;
  for (let i = 0; i < stages.length; i++) {
    if (elapsed >= stages[i].at) activeIdx = i;
  }
  const active = stages[activeIdx];
  const detail = active.details[Math.floor((elapsed - active.at) / 4) % active.details.length];

  // If live, estimate the progress bar fill based on liveSteps progress,
  // otherwise use the simulated progress.
  let pct = 0;
  if (!isLive) {
    pct = Math.min(92, Math.round(((activeIdx + 1) / stages.length) * 88 + Math.min(elapsed / 10, 4)));
  } else {
    // There are 5 expected real stages (received, parsing, extracting, mapping, finalizing)
    const totalExpectedStages = 5;
    pct = Math.min(98, Math.round((liveSteps.length / totalExpectedStages) * 60 + 35));
  }

  // Determine the current milestone text and detail
  let currentMilestone = '';
  let currentMilestoneDetail = '';
  if (!isLive) {
    currentMilestone = active.label;
    currentMilestoneDetail = detail;
  } else {
    const activeLive = liveSteps.find(s => !s.done) || liveSteps[liveSteps.length - 1];
    currentMilestone = activeLive ? activeLive.label : 'Processing...';
    currentMilestoneDetail = activeLive ? (activeLive.detail || '') : '';
  }

  return (
    <div className="spl-bubble spl-bubble--agent spl-bubble--live spl-sim" style={{ padding: '16px' }}>
      <div className="spl-sim-head">
        <span className="spl-sim-title">
          {liveText ? 'Generating response' : 'Extracting your document'}
        </span>
        <span className="spl-sim-elapsed">{elapsed}s</span>
      </div>

      {!liveText && (
        <>
          <div className="spl-sim-bar" style={{ margin: '8px 0' }}>
            <div className="spl-sim-bar-fill" style={{ width: `${pct}%` }} />
          </div>
          <div className="spl-sim-milestone" style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px', color: '#e2e8f0', marginTop: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span className="spl-step-spinner" style={{ width: '14px', height: '14px', borderWidth: '2px', borderStyle: 'solid', borderColor: 'rgba(126, 179, 255, 0.25)', borderTopColor: '#7eb3ff', borderRadius: '50%', flexShrink: 0 }} />
              <span style={{ fontWeight: 600 }}>{currentMilestone}</span>
            </div>
            {currentMilestoneDetail && (
              <div className="spl-sim-detail" style={{ color: '#8a96b0', paddingLeft: '22px', fontSize: '12px', animation: 'none', whiteSpace: 'normal', lineHeight: '1.4' }}>
                {currentMilestoneDetail}
              </div>
            )}
          </div>

        </>
      )}

      {liveText && (
        <div className="spl-livetext-container" style={{ marginTop: '4px' }}>
          <div className="spl-livetext">
            <MarkdownText text={cleanExtractionText(liveText)} />
            <span className="spl-caret" aria-hidden="true" />
          </div>
          <div className="spl-generating">Generating…</div>
        </div>
      )}
    </div>
  );
};


// ── Follow-up suggestion pools (used when the agent returns no `hints`) ───────

const SUGGEST_RESULT = [
  'What warehouse did that product map to?',
  'Which fields were left empty?',
  'Show me the full JSON for that extraction.',
  'Are there any validation errors?',
  'What category was this product assigned?',
  'List all the extracted attributes.',
  'What unit of measure was detected?',
  'Did any rows fail to extract?',
];

const SUGGEST_GENERAL = [
  'How do I extract a supplier document?',
  'Which file formats are supported?',
  'What information do you pull from a document?',
  'Summarise what you can do.',
];

const rotate4 = (pool: string[], turn: number): string[] => {
  const out: string[] = [];
  for (let i = 0; out.length < 4 && i < pool.length; i++) out.push(pool[(turn + i) % pool.length]);
  return Array.from(new Set(out)).slice(0, 4);
};

// ── Component ────────────────────────────────────────────────────────────────

const StepProductLinq: React.FC = () => {
  const { instance, accounts } = useMsal();
  const accountName = accounts?.[0]?.username || accounts?.[0]?.name || '';

  // Acquire an AAD access token for the API scope. Backend rejects requests
  // without it ("Unauthorized — valid AAD token required"). Returns null when a
  // redirect was triggered (silent acquisition failed) — caller must bail out.
  const acquireApiToken = useCallback(async (): Promise<string | null> => {
    try {
      const tokenResponse = await instance.acquireTokenSilent({
        ...apiRequest,
        account: accounts[0],
      });
      return tokenResponse.accessToken;
    } catch {
      await instance.acquireTokenRedirect(apiRequest);
      return null;
    }
  }, [instance, accounts]);

  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [opco, setOpco] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [attachError, setAttachError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);       // JSON copy
  const [copiedMsgId, setCopiedMsgId] = useState<string | null>(null); // [P0] message copy
  const [conversationId, setConversationId] = useState<string>(newUUID);
  const [cfgOpen, setCfgOpen] = useState(false);
  const [promptCfgOpen, setPromptCfgOpen] = useState(false);
  const [tier2CfgOpen, setTier2CfgOpen] = useState(false);
  const [liveSteps, setLiveSteps] = useState<LiveStep[]>([]);
  const [liveText, setLiveText] = useState('');
  const [simCtx, setSimCtx] = useState<{ fileName: string; opco: string } | null>(null);
  const [postStates, setPostStates] = useState<Record<string, PostState>>({});
  // [P0] new UI state
  const [dragActive, setDragActive] = useState(false);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const [showNewChatConfirm, setShowNewChatConfirm] = useState(false);
  const [srMsg, setSrMsg] = useState(''); // aria-live announcement between turns
  const [sessionCopied, setSessionCopied] = useState(false);

  // UI-only gate for the sensitive "Post to STEP" write; APIM enforces the real
  // check on /webhook/step-product-upload.
  const { hasRole, roles, isSuperuser } = useAuthorization();
  const canPostToStep = hasRole(Roles.StepPostToStep);
  const myOpcos = useMemo(() => allowedOpcos(roles, isSuperuser), [roles, isSuperuser]);
  const [opcoOpen, setOpcoOpen] = useState(false);
  const opcoDdRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (myOpcos.length === 1 && opco !== myOpcos[0]) setOpco(myOpcos[0]);
  }, [myOpcos, opco]);
  // Close the OpCo dropdown on outside click / Escape.
  useEffect(() => {
    if (!opcoOpen) return;
    const onDown = (e: MouseEvent) => {
      if (opcoDdRef.current && !opcoDdRef.current.contains(e.target as Node)) setOpcoOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpcoOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [opcoOpen]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const opcoRef = useRef<HTMLButtonElement>(null);      // [P0] focus target (OpCo trigger)
  const chatRef = useRef<HTMLDivElement>(null);         // [P0] scroll container
  const abortRef = useRef<AbortController | null>(null);
  const tokensRef = useRef('');                         // [P0] partial tokens for Stop
  const lastRequestRef = useRef<LastRequest | null>(null); // [P0] Retry
  const nearBottomRef = useRef(true);                   // [P0] auto-scroll gate
  const dragDepth = useRef(0);                          // [P0] enter/leave counter
  const hasResultRef = useRef(false);                   // a STEP payload has been generated

  const webhookUrl = process.env.REACT_APP_PRODUCT_AGENT_WEBHOOK_URL + '/webhook/step-product-chat';
  const uploadUrl = process.env.REACT_APP_PRODUCT_AGENT_WEBHOOK_URL + '/webhook/step-product-upload';

  useEffect(() => { textareaRef.current?.focus(); }, []);

  // Track whether a STEP payload has been generated in this conversation.
  useEffect(() => { hasResultRef.current = messages.some((m) => m.payload !== undefined); }, [messages]);

  // [P0] Smart auto-scroll — only pin to bottom when already near it.
  useEffect(() => {
    if (nearBottomRef.current) {
      const el = chatRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    }
  }, [messages, isLoading, liveSteps, liveText]);

  // [P0] Re-pin on layout shifts that `messages` can't see. The composer is a
  // flex sibling of the scroller inside a fixed-height wrap, so it steals
  // height from the chat as the textarea autogrows, the attach/suggestion rows
  // mount, or the window resizes — the newest turn slides out of view with no
  // state change to trigger the effect above. Observing the scroller catches
  // the viewport shrinking; observing its child catches content growing
  // (streamed markdown, an expanded result block).
  const chatIsEmpty = messages.length === 0;
  useEffect(() => {
    const el = chatRef.current;
    if (!el) return;

    const pin = () => { if (nearBottomRef.current) el.scrollTop = el.scrollHeight; };

    window.addEventListener('resize', pin);
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(pin);
      ro.observe(el);
      if (el.firstElementChild) ro.observe(el.firstElementChild);
    }
    return () => { ro?.disconnect(); window.removeEventListener('resize', pin); };
  }, [chatIsEmpty]); // re-observe when the welcome pane swaps for the message list

  useEffect(() => () => abortRef.current?.abort(), []);

  const handleChatScroll = useCallback(() => {
    const el = chatRef.current;
    if (!el) return;
    const near = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
    nearBottomRef.current = near;
    setShowScrollBtn(!near);
  }, []);

  const scrollToBottom = useCallback(() => {
    const el = chatRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    nearBottomRef.current = true;
    setShowScrollBtn(false);
  }, []);

  const resetTextarea = () => {
    if (textareaRef.current) { textareaRef.current.value = ''; textareaRef.current.style.height = 'auto'; }
  };

  // ── File attach ──
  const acceptFile = useCallback((list: FileList | File[] | null | undefined) => {
    const incoming = list ? Array.from(list) : [];
    if (incoming.length === 0) return;
    // [P0] one at a time
    if (incoming.length > 1) {
      setAttachError('Please attach one document at a time.');
      return;
    }
    const f = incoming[0];
    // [P0] extension whitelist (drag/paste bypass the file-picker `accept`)
    const okExt = ACCEPT_EXT.some((ext) => f.name.toLowerCase().endsWith(ext));
    if (!okExt) {
      setAttachError(`"${f.name}" isn't a supported type. Attach an Excel, CSV or PDF file.`);
      return;
    }
    if (f.size > MAX_BYTES) {
      setAttachError(`"${f.name}" too large (${formatBytes(f.size)}). Max 25 MB.`);
      return;
    }
    setFile(f);
    setAttachError(null);
    // [P0] autofocus OpCo the moment a file is staged
    setTimeout(() => opcoRef.current?.focus(), 60);
  }, []);

  const clearFile = useCallback(() => {
    setFile(null);
    setOpco('');
    setAttachError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  // [P0] Drag-and-drop over the whole chat surface.
  const onDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (isLoading) return;
    dragDepth.current += 1;
    setDragActive(true);
  }, [isLoading]);
  const onDragOver = useCallback((e: React.DragEvent) => { e.preventDefault(); }, []);
  const onDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragDepth.current -= 1;
    if (dragDepth.current <= 0) { dragDepth.current = 0; setDragActive(false); }
  }, []);
  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragDepth.current = 0;
    setDragActive(false);
    if (isLoading) return;
    acceptFile(e.dataTransfer?.files);
  }, [isLoading, acceptFile]);

  // [P0] Paste a file straight into the composer.
  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const files = e.clipboardData?.files;
    if (files && files.length > 0) { e.preventDefault(); acceptFile(files); }
  }, [acceptFile]);

  // ── Response helpers ──
  const appendAgentReply = useCallback((parsed: ParsedResult, meta?: { fileName?: string; opcoUsed?: string }) => {
    setMessages((prev) => [
      ...prev,
      {
        id: newUUID(),
        role: 'agent',
        text: cleanExtractionText(parsed.text) || 'Done.',
        timestamp: formatTime(new Date()),
        ...(parsed.json !== undefined && { payload: stripProductIds(parsed.json) }),
        ...(parsed.hints && { hints: parsed.hints }),
        ...(meta?.fileName && { fileName: meta.fileName }),
        ...(meta?.opcoUsed && { opcoUsed: meta.opcoUsed }),
      },
    ]);
    setSrMsg('Response complete.'); // [A11Y]
  }, []);

  const appendAgentError = useCallback((text: string) => {
    setMessages((prev) => [...prev, { id: newUUID(), role: 'agent', text, timestamp: formatTime(new Date()), isError: true }]);
    setSrMsg(text); // [A11Y]
  }, []);

  const friendlyCatch = (err: any): string => {
    const msg = err?.message ?? '';
    return msg === 'CONFIG_MISSING'
      ? 'Configuration Error: REACT_APP_PRODUCT_AGENT_WEBHOOK_URL is not set.'
      : `Connection Error: unable to reach the workflow. (${msg})`;
  };

  // ── Core send (parameterised so Retry can re-run the same inputs) ──
  const runSend = useCallback(async (rawText: string, theFile: File | null, theOpco: string) => {
    if (isLoading) return;
    const text = (rawText || '').trim();

    if (theFile) {
      // [P0] Missing OpCo: focus the field, DON'T post an error banner.
      if (!theOpco.trim()) { opcoRef.current?.focus(); return; }
    } else if (!text) {
      return;
    }

    // Block chat-driven edits to an already-generated payload. Echo the user's
    // turn, then answer with a canned explanation — never reaches the workflow.
    if (!theFile && text && hasResultRef.current && CHANGE_INTENT_RE.test(text)) {
      const now = new Date();
      setMessages((prev) => [
        ...prev,
        { id: newUUID(), role: 'user', text, timestamp: formatTime(now) },
        { id: newUUID(), role: 'agent', text: CHANGE_BLOCKED_MSG, timestamp: formatTime(now) },
      ]);
      setInputValue('');
      resetTextarea();
      setSrMsg('Editing the generated payload is not supported.');
      return;
    }

    lastRequestRef.current = { text, file: theFile, opco: theOpco }; // [P0] for Retry
    tokensRef.current = '';                                          // [P0] for Stop
    const meta = theFile ? { fileName: theFile.name, opcoUsed: theOpco } : undefined;
    let succeeded = false; // [P0] only consume the attachment on success

    const userText = theFile
      ? `${text || EXTRACT_PROMPT}\n\n📎 ${theFile.name} · OpCo ${theOpco}`
      : text;
    setMessages((prev) => [...prev, { id: newUUID(), role: 'user', text: userText, timestamp: formatTime(new Date()), ...(theFile && { fileName: theFile.name, opcoUsed: theOpco }) }]);
    setInputValue('');
    resetTextarea();
    nearBottomRef.current = true; // sending always jumps to the new turn
    setIsLoading(true);
    setSrMsg('Working…');
    if (theFile) setSimCtx({ fileName: theFile.name, opco: theOpco.trim() });

    try {
      if (!webhookUrl) throw new Error('CONFIG_MISSING');

      const token = await acquireApiToken();
      if (!token) return; // redirect triggered — bail out

      const controller = new AbortController();
      abortRef.current = controller;

      // Build a FRESH request each attempt — FormData / bodies are single-use, so
      // fetchWithRetry re-invokes this thunk when it retries a transient 5xx.
      const makeRequest = (): Promise<Response> => {
        if (theFile) {
          const fd = new FormData();
          fd.append('request_payload', text || EXTRACT_PROMPT);
          fd.append('opco', theOpco.trim());
          fd.append('user', accountName.trim());
          fd.append('data', theFile, theFile.name);
          return fetch(webhookUrl, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'x-ms-conversationid': conversationId },
            body: fd,
            signal: controller.signal,
          });
        }
        return fetch(webhookUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'x-ms-conversationid': conversationId },
          body: JSON.stringify({ request_payload: text }),
          signal: controller.signal,
        });
      };

      const res = await fetchWithRetry(makeRequest, {
        signal: controller.signal,
        onRetry: () => {
          // n8n is still coming up (or a revision is rolling over) — show a
          // transient step instead of a hard error while we back off and retry.
          setLiveSteps([{ stage: 'starting', label: 'Service is starting up — retrying…', done: false }]);
          setSrMsg('Service is starting up — retrying'); // [A11Y]
        },
      });

      if (res.ok && isStreamingResponse(res)) {
        let settled = false;
        let tokens = '';
        await readN8nStream(res, (e: StreamEvent) => {
          if (e.kind === 'status') {
            setLiveSteps((prev) => [
              ...prev.map((s) => ({ ...s, done: true })),
              { stage: e.stage, label: e.label, ...(e.detail && { detail: e.detail }), done: false },
            ]);
            setSrMsg(e.label); // [A11Y] announce each stage
          } else if (e.kind === 'token') {
            tokens += e.text;
            tokensRef.current = tokens; // [P0] keep for Stop
            setLiveText((t) => t + e.text);
          } else if (e.kind === 'final') {
            settled = true;
            succeeded = true;
            setLiveSteps([]); setLiveText('');
            appendAgentReply(
              { text: e.output || tokens, ...(e.json !== undefined && { json: e.json }), ...(e.hints && { hints: e.hints }) },
              meta,
            );
          } else {
            settled = true;
            setLiveSteps([]); setLiveText('');
            // `code` is UPPER_SNAKE per the 2026-08-03 contract — add a
            // remediation line when we recognise it, keep the server text first.
            appendAgentError(describeStepError(e.code, e.message));
          }
        });
        if (!settled) {
          if (tokens.trim()) { appendAgentReply({ text: tokens }, meta); succeeded = true; }
          else appendAgentError('The workflow stream ended unexpectedly — please retry.');
        }
      } else {
        const parsed = await parseResult(res);
        if (!res.ok) {
          // A transient 5xx that survived all retries — show actionable copy, not
          // the backend's raw "Database is not ready!" message.
          const msg = TRANSIENT_STATUS.has(res.status)
            ? TRANSIENT_MESSAGE
            : parsed.text || (res.status === 422 ? 'Document rejected — check OpCo / user and retry.' : `Workflow error (HTTP ${res.status}).`);
          appendAgentError(msg);
        } else {
          appendAgentReply(parsed, meta);
          succeeded = true;
        }
      }
      // [P0] Only consume the attachment when the turn actually succeeded, so a
      // 422 / AWAITING_OPCO keeps the file staged for a one-click retry.
      if (theFile && succeeded) clearFile();
    } catch (err: any) {
      if (err?.name === 'AbortError') {
        // [P0] Stop: materialise whatever streamed so far as a stopped message.
        const partial = tokensRef.current.trim();
        if (partial) appendAgentReply({ text: `${partial}\n\n_(stopped)_` }, meta);
      } else {
        appendAgentError(friendlyCatch(err));
      }
    } finally {
      abortRef.current = null;
      setLiveSteps([]);
      setLiveText('');
      setSimCtx(null);
      setIsLoading(false);
      textareaRef.current?.focus();
    }
  }, [isLoading, webhookUrl, accountName, conversationId, appendAgentReply, appendAgentError, clearFile, acquireApiToken]);

  const handleSend = useCallback(() => {
    runSend(inputValue, file, opco);
  }, [runSend, inputValue, file, opco]);

  // [P0] Stop generating.
  const handleStop = useCallback(() => { abortRef.current?.abort(); }, []);

  // [P0] Retry the last request with the same inputs.
  const retryLast = useCallback(() => {
    const r = lastRequestRef.current;
    if (r) runSend(r.text, r.file, r.opco);
  }, [runSend]);

  const doNewChat = useCallback(() => {
    abortRef.current?.abort();
    setMessages([]);
    setInputValue('');
    setFile(null);
    setOpco('');
    setAttachError(null);
    setPostStates({});
    setConversationId(newUUID());
    setShowNewChatConfirm(false);
    setShowScrollBtn(false);
    nearBottomRef.current = true;
    resetTextarea();
    if (fileInputRef.current) fileInputRef.current.value = '';
    setTimeout(() => textareaRef.current?.focus(), 50);
  }, []);

  // [P1] Confirm before wiping a non-empty conversation.
  const requestNewChat = useCallback(() => {
    if (messages.length > 0 || isLoading) setShowNewChatConfirm(true);
    else doNewChat();
  }, [messages.length, isLoading, doNewChat]);

  // ── Copy / Download STEP JSON ──
  const copyJson = useCallback((payload: unknown, id: string) => {
    navigator.clipboard.writeText(JSON.stringify(payload, null, 2)).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId((c) => (c === id ? null : c)), 1500);
    });
  }, []);

  // [P0] Copy a whole agent message.
  const copyMessage = useCallback((m: ChatMsg) => {
    navigator.clipboard.writeText(m.text || '').then(() => {
      setCopiedMsgId(m.id);
      setTimeout(() => setCopiedMsgId((c) => (c === m.id ? null : c)), 1500);
    });
  }, []);

  // [P1] Click-to-copy the conversation id.
  const copySession = useCallback(() => {
    navigator.clipboard.writeText(conversationId).then(() => {
      setSessionCopied(true);
      setTimeout(() => setSessionCopied(false), 1500);
    });
  }, [conversationId]);

  const applyProductIds = useCallback((messageId: string, ids: string[]) => {
    setMessages((prev) => prev.map((m) => {
      if (m.id !== messageId || !isObj(m.payload)) return m;
      const payload = m.payload as Record<string, unknown>;
      if (!Array.isArray(payload['products'])) return m;
      const products = (payload['products'] as unknown[]).map((p, i) =>
        isObj(p) && typeof ids[i] === 'string' ? { ...p, id: ids[i] } : p,
      );
      return { ...m, payload: { ...payload, products } };
    }));
  }, []);

  const postToStep = useCallback(async (m: ChatMsg) => {
    const setState = (s: PostState) => setPostStates((prev) => ({ ...prev, [m.id]: s }));
    if (!uploadUrl) {
      setState({ status: 'error', message: 'Configuration Error: REACT_APP_PRODUCT_AGENT_WEBHOOK_URL is not set.' });
      return;
    }
    setState({ status: 'posting' });
    try {
      const token = await acquireApiToken();
      if (!token) return; // redirect triggered — bail out
      const res = await fetchWithRetry(() => fetch(uploadUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'x-ms-conversationid': conversationId },
        body: JSON.stringify({ payload: m.payload, opco: m.opcoUsed || '', user: accountName.trim() }),
      }));
      // Upload proxy no longer always returns 200 — a 400/502 now carries an error
      // envelope. `data.message` is the display string; since 2026-08-03 it also
      // names the packaging-tier count (previously always 0, so the sentence never
      // appeared) and the legacy `dropped_containers` field is gone.
      const data = await unwrapEnvelope<UploadResult>(res);
      const message = typeof data?.message === 'string' ? data.message : '';
      setState({ status: 'done', ...(message && { message }) });
    } catch (err: any) {
      // STEP_AUTH_FAILED / STEP_UPLOAD_FAILED / STEP_REJECTED / VALIDATION_ERROR
      // carry a server message; anything else is a transport failure.
      setState(err instanceof StepApiError
        ? { status: 'error', message: TRANSIENT_STATUS.has(err.status) ? TRANSIENT_MESSAGE : describeStepError(err.code, err.message) }
        : { status: 'error', message: `Connection Error: unable to reach the upload workflow. (${err?.message ?? ''})` });
    }
  }, [uploadUrl, conversationId, accountName, acquireApiToken]);

  const downloadJson = useCallback((m: ChatMsg) => {
    const blob = new Blob([JSON.stringify(m.payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileSlug(m.opcoUsed || '')}_${fileSlug(m.fileName || 'export')}_${fileStamp(new Date())}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (isLoading) return; // [P1] composer stays usable, but Enter waits for the current turn
      handleSend();
    }
  };
  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    e.target.style.height = '1px';
    e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
  };

  const canSend = !isLoading && (file ? !!opco.trim() : !!inputValue.trim());

  const activeSuggestions = useMemo<string[]>(() => {
    const agentTurns = messages.filter((m) => m.role === 'agent');
    const lastAgent = agentTurns[agentTurns.length - 1];
    if (lastAgent?.hints && lastAgent.hints.length > 0) return lastAgent.hints.slice(0, 4);
    const hasResult = messages.some((m) => m.payload !== undefined);
    return rotate4(hasResult ? SUGGEST_RESULT : SUGGEST_GENERAL, agentTurns.length);
  }, [messages]);

  return (
    <Layout title="Product data Onboarding Agent for STEP">
      {/* [P0] Drag-and-drop attach over the whole workflow surface. */}
      <div
        className="spl-wrap"
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        {/* [A11Y] polite status region — announces stage changes + completion */}
        <div className="spl-sr-only" aria-live="polite" role="status">
          {isLoading ? (liveSteps[liveSteps.length - 1]?.label || (simCtx ? 'Extracting your document' : 'Working')) : srMsg}
        </div>

        {/* [P0] Full-surface drop overlay */}
        {dragActive && (
          <div className="spl-dropzone" aria-hidden="true">
            <div className="spl-dropzone-inner">
              <div className="spl-dropzone-icon"><MdCloudUpload aria-hidden="true" /></div>
              <div className="spl-dropzone-title">Drop to attach</div>
              <div className="spl-dropzone-sub">Excel, CSV or PDF · up to 25&nbsp;MB</div>
            </div>
          </div>
        )}

        {/* ── Header ── */}
        <div className="spl-header">
          <div className="spl-header-left">
            <div className="spl-header-icon"><MdDescription aria-hidden="true" /></div>
            <div>
              <p className="spl-header-sub">Extract supplier documents · Map to STEP · Ask follow-ups</p>
            </div>
          </div>
          <div className="spl-header-right">
            {/* [P1] Session badge is now click-to-copy */}
            <button
              className="spl-session-badge spl-session-badge--btn"
              onClick={copySession}
              title={`Copy conversation id: ${conversationId}`}
              aria-label="Copy conversation id"
            >
              {sessionCopied ? <MdCheck aria-hidden="true" /> : <MdContentCopy aria-hidden="true" />}
              {sessionCopied ? 'Copied id' : <>Session&nbsp;{conversationId.slice(0, 8)}</>}
            </button>
            <Can role={Roles.StepPromptConfig}>
              <button className="spl-cfg-btn" onClick={() => setPromptCfgOpen(true)} title="Edit the prompt rule blocks assembled into the extraction prompt">
                <MdArticle aria-hidden="true" />Prompt Config
              </button>
            </Can>
            <Can role={Roles.StepAttributes}>
              <button className="spl-cfg-btn" onClick={() => setCfgOpen(true)} title="Edit the STEP attribute list injected into the extraction prompt">
                <MdSettings aria-hidden="true" />Attributes
              </button>
            </Can>
            <Can role={Roles.StepTier2MasterData}>
              <button className="spl-cfg-btn" onClick={() => setTier2CfgOpen(true)} title="Publish the OpCo Tier 2 master-data hierarchies injected into the extraction prompt">
                <MdInventory2 aria-hidden="true" />Tier 2 Master Data
              </button>
            </Can>
            {/* [P1] New Chat with inline confirm popover */}
            <div className="spl-newchat-wrap">
              <button className="spl-new-btn" onClick={requestNewChat} title="Start a new chat (fresh memory)">
                <MdAdd aria-hidden="true" />New Chat
              </button>
              {showNewChatConfirm && (
                <div className="spl-confirm-pop" role="dialog" aria-label="Start a new chat">
                  <p className="spl-confirm-title">Start a new chat?</p>
                  <p className="spl-confirm-desc">This clears the current conversation and its memory. This can't be undone.</p>
                  <div className="spl-confirm-actions">
                    <button className="spl-confirm-cancel" onClick={() => setShowNewChatConfirm(false)}>Cancel</button>
                    <button className="spl-confirm-ok" onClick={doNewChat}>New chat</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Chat area ── */}
        <div className="spl-chat" ref={chatRef} onScroll={handleChatScroll}>
          {messages.length === 0 ? (
            <div className="spl-welcome">
              <div className="spl-welcome-icon"><MdDescription aria-hidden="true" /></div>
              <h2 className="spl-welcome-title">Product Data Onboarding</h2>
              <p className="spl-welcome-desc">
                Drop a supplier document to extract it into STEP product data — or ask a question below.
              </p>
              {/* [P1] Empty state doubles as a drop zone / browse button */}
              <button
                type="button"
                className="spl-welcome-drop"
                onClick={() => fileInputRef.current?.click()}
              >
                <MdCloudUpload aria-hidden="true" className="spl-welcome-drop-icon" />
                <span className="spl-welcome-drop-title">Drag &amp; drop, paste, or <u>browse files</u></span>
                <span className="spl-welcome-drop-sub">Excel, CSV or PDF · up to 25&nbsp;MB</span>
              </button>
              <div className="spl-welcome-chips">
                {SUGGEST_GENERAL.slice(0, 3).map((s) => (
                  <button
                    key={s}
                    className="spl-suggestion"
                    onClick={() => { setInputValue(s); setTimeout(() => textareaRef.current?.focus(), 0); }}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="spl-messages" role="log" aria-label="Conversation" aria-live="off">
              {messages.map((m) => {
                const hasResult = m.role === 'agent' && m.payload !== undefined;
                return (
                  <div key={m.id} className={`spl-row spl-row--${m.role}`}>
                    {m.role === 'agent' && (
                      <div className={`spl-avatar${m.isError ? ' spl-avatar--error' : ''}`}>
                        {m.isError ? <MdErrorOutline aria-hidden="true" /> : <MdDescription aria-hidden="true" />}
                      </div>
                    )}
                    <div className={`spl-msg-wrap spl-msg-wrap--${m.role}${hasResult ? ' spl-msg-wrap--wide' : ''}`}>
                      <div
                        className={`spl-bubble spl-bubble--${m.role}${m.isError ? ' spl-bubble--error' : ''}`}
                        {...(m.isError ? { role: 'alert' } : {})}
                      >
                        {m.role === 'agent' ? <MarkdownText text={m.text} collapsible /> : m.text}
                        {/* [P0] copy anchored inside the bubble — unambiguous which
                            message it belongs to when turns stack in sequence. */}
                        {!m.isError && (
                          <button
                            className={`spl-msg-copy spl-msg-copy--in${copiedMsgId === m.id ? ' is-copied' : ''}`}
                            onClick={() => copyMessage(m)}
                            aria-label={copiedMsgId === m.id ? 'Message copied' : 'Copy message'}
                            title="Copy message"
                          >
                            {copiedMsgId === m.id ? <MdCheck aria-hidden="true" /> : <MdContentCopy aria-hidden="true" />}
                          </button>
                        )}
                      </div>
                      {/* Meta (time + retry) sits directly under the message,
                          above the STEP JSON block. */}
                      <div className={`spl-meta spl-meta--${m.role}`}>
                        <span className="spl-time">{m.timestamp}</span>
                        {/* [P0] retry on error */}
                        {m.isError && (
                          <button className="spl-retry-btn" onClick={retryLast} title="Retry the last request">
                            <MdRefresh aria-hidden="true" />Retry
                          </button>
                        )}
                      </div>
                      {hasResult && (
                        <ResultBlock
                          payload={m.payload}
                          copied={copiedId === m.id}
                          postState={postStates[m.id]}
                          canPost={canPostToStep}
                          onCopy={() => copyJson(m.payload, m.id)}
                          onDownload={() => downloadJson(m)}
                          onApplyIds={(ids) => applyProductIds(m.id, ids)}
                          onPost={() => postToStep(m)}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
              {isLoading && (
                <div className="spl-row spl-row--agent">
                  <div className="spl-avatar"><MdDescription aria-hidden="true" /></div>
                  <div className="spl-msg-wrap spl-msg-wrap--agent">
                    {simCtx ? (
                      <UnifiedLoader
                        fileName={simCtx.fileName}
                        opco={simCtx.opco}
                        liveSteps={liveSteps}
                        liveText={liveText}
                      />
                    ) : (
                      liveText ? (
                        <div className="spl-bubble spl-bubble--agent spl-bubble--live">
                          <div className="spl-livetext">
                            <MarkdownText text={cleanExtractionText(liveText)} />
                            <span className="spl-caret" aria-hidden="true" />
                          </div>
                          <div className="spl-generating">Generating…</div>
                        </div>
                      ) : (
                        <div className="spl-bubble spl-bubble--agent spl-bubble--typing">
                          <span className="spl-dot" /><span className="spl-dot" /><span className="spl-dot" />
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* [P0] Scroll-to-bottom pill */}
        {showScrollBtn && (
          <button className="spl-scroll-pill" onClick={scrollToBottom} aria-label="Scroll to latest">
            <MdArrowDownward aria-hidden="true" />Latest
          </button>
        )}

        {/* ── Composer ── */}
        <div className="spl-input-area">
          {attachError && (
            <div className="spl-attach-error" role="alert">
              <MdErrorOutline className="spl-attach-error-icon" aria-hidden="true" />
              <span className="spl-attach-error-msg">{attachError}</span>
              <button
                type="button"
                className="spl-attach-error-close"
                onClick={() => setAttachError(null)}
                aria-label="Dismiss error"
                title="Dismiss"
              >
                <MdClose aria-hidden="true" />
              </button>
            </div>
          )}

          {!isLoading && activeSuggestions.length > 0 && messages.length > 0 && (
            <div className="spl-suggestions">
              {activeSuggestions.map((s) => (
                <button
                  key={s}
                  className="spl-suggestion"
                  onClick={() => { setInputValue(s); setTimeout(() => textareaRef.current?.focus(), 0); }}
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {file && (
            <div className="spl-attach-row">
              <div className="spl-attach-chip">
                <MdInsertDriveFile aria-hidden="true" className="spl-attach-chip-icon" />
                <span className="spl-attach-name" title={file.name}>{file.name}</span>
                <span className="spl-attach-size">{formatBytes(file.size)}</span>
                <button className="spl-attach-remove" onClick={clearFile} disabled={isLoading} aria-label="Remove file"><MdClose aria-hidden="true" /></button>
              </div>
              {/* OpCo is required, and constrained to the user's assigned OpCos */}
              <span className={`spl-opco-inline${opco.trim() ? ' spl-opco-inline--ok' : ' spl-opco-inline--req'}`}>
                OpCo <span className="spl-opco-star" aria-hidden="true">*</span>
                {myOpcos.length === 0 ? (
                  <span className="spl-opco-none" role="note">
                    No OpCo assigned — contact your administrator
                  </span>
                ) : (
                  <div className="spl-opco-dd" ref={opcoDdRef}>
                    <button
                      ref={opcoRef}
                      type="button"
                      className={`spl-opco-input spl-opco-trigger${opco ? '' : ' spl-opco-trigger--ph'}`}
                      onClick={() => setOpcoOpen((o) => !o)}
                      aria-haspopup="listbox"
                      aria-expanded={opcoOpen}
                      aria-label="OpCo (required)"
                    >
                      <span className="spl-opco-value">{opco || 'Select OpCo…'}</span>
                      <MdKeyboardArrowRight className="spl-opco-caret" data-open={opcoOpen ? 'true' : 'false'} aria-hidden="true" />
                    </button>
                    {opcoOpen && (
                      <ul className="spl-opco-menu" role="listbox" aria-label="OpCo">
                        {myOpcos.map((o) => (
                          <li
                            key={o}
                            role="option"
                            aria-selected={o === opco}
                            className={`spl-opco-opt${o === opco ? ' spl-opco-opt--sel' : ''}`}
                            onClick={() => { setOpco(o); setOpcoOpen(false); opcoRef.current?.focus(); }}
                          >
                            <span>{o}</span>
                            {o === opco && <MdCheck className="spl-opco-check" aria-hidden="true" />}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </span>
            </div>
          )}

          <div className="spl-input-inner">
            <input ref={fileInputRef} type="file" accept={ACCEPT} className="spl-file-input"
              onChange={(e) => acceptFile(e.target.files)} disabled={isLoading} />
            <button
              className="spl-attach-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={isLoading}
              title="Attach a supplier document"
              aria-label="Attach file"
            >
              <MdUploadFile aria-hidden="true" />
            </button>
            {/* [P1] textarea stays enabled while a turn streams */}
            <textarea
              ref={textareaRef}
              className="spl-textarea"
              placeholder={file ? 'Add a note for the extraction (optional)…' : "Attach a document, or ask a follow-up — e.g. 'What warehouse did that product map to?'"}
              value={inputValue}
              onChange={handleInput}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              rows={1}
              aria-label="Message"
            />
            {/* [P0] Send morphs into Stop while generating */}
            {isLoading ? (
              <button className="spl-stop-btn" onClick={handleStop} aria-label="Stop generating" title="Stop generating">
                <MdStop aria-hidden="true" />
              </button>
            ) : (
              <button className="spl-send-btn" onClick={handleSend} disabled={!canSend} aria-label="Send">
                <MdSend aria-hidden="true" />
              </button>
            )}
          </div>

          <p className="spl-input-hint">
            {isLoading
              ? 'Generating… press Stop to cancel · you can keep typing your next message'
              : 'Attach a file + OpCo to extract · Enter to send · Shift+Enter for new line'}
          </p>
        </div>

      </div>

      <AttributeConfig open={cfgOpen} onClose={() => setCfgOpen(false)} updatedBy={accountName} />
      <PromptConfig open={promptCfgOpen} onClose={() => setPromptCfgOpen(false)} updatedBy={accountName} />
      <Tier2Config open={tier2CfgOpen} onClose={() => setTier2CfgOpen(false)} updatedBy={accountName} />
    </Layout>
  );
};

export default StepProductLinq;
